import {
	SET_REMINDERS,
	CREATE_REMINDER,
	UPDATE_REMINDER,
	DELETE_REMINDER,
	SET_REMINDERS_LOADING,
	SET_REMINDERS_ERROR,
} from './types';

import { format } from 'date-fns';
import axios from 'axios';
import { LIST_TAG, remindersApi } from '@/slices/remindersApi';

// The Reminders page caches its own list, so tell it to refetch after calendar changes
const refreshRemindersPage = (dispatch) =>
	dispatch(remindersApi.util.invalidateTags([LIST_TAG]));

export const setReminders = (reminders) => {
	return {
		type: SET_REMINDERS,
		payload: reminders,
	};
};

export const fetchAllReminders = ({ dateRange }) => {
	return async (dispatch) => {
		// Format dates for API call
		const fromDate = dateRange?.from
			? format(dateRange.from, 'yyyy-MM-dd')
			: '1970-01-01';
		const toDate = dateRange?.to
			? format(dateRange.to, 'yyyy-MM-dd')
			: format(new Date(), 'yyyy-MM-dd');

		try {
			dispatch({ type: SET_REMINDERS_LOADING, payload: true });

			const response = await axios.get(
				`/api/reminders/list/${fromDate}/${toDate}`
			);

			if (response.data && response.data.reminders) {
				dispatch(setReminders(response.data.reminders));
			} else {
				dispatch({
					type: SET_REMINDERS_ERROR,
					payload:
						response.data?.message ||
						'Failed to fetch reminders or malformed response',
				});
			}
		} catch (error) {
			dispatch({ type: SET_REMINDERS_ERROR, payload: error.message });
		} finally {
			dispatch({ type: SET_REMINDERS_LOADING, payload: false });
		}
	};
};

export const createReminder = (reminderData) => async (dispatch) => {
	try {
		dispatch({ type: SET_REMINDERS_LOADING, payload: true });
		const response = await axios.post(
			`/api/reminders/create`,
			reminderData
		);

		if (response.data && response.data.error === false) {
			const from = new Date();
			from.setMonth(from.getMonth() - 1);
			const to = new Date();
			to.setMonth(to.getMonth() + 1);
			await dispatch(fetchAllReminders({ dateRange: { from, to } }));
			refreshRemindersPage(dispatch);
		} else {
			const errorMessage =
				response.data?.message ||
				'Failed to create reminder: Invalid data from server';
			console.error(
				'createReminder: Error or invalid data received from API',
				response.data
			);
			dispatch({ type: SET_REMINDERS_ERROR, payload: errorMessage });
			dispatch({ type: SET_REMINDERS_LOADING, payload: false });
		}
	} catch (error) {
		console.error(
			'Error in createReminder action:',
			error.response || error.message || error
		);
		dispatch({
			type: SET_REMINDERS_ERROR,
			payload:
				error.response?.data?.message ||
				error.message ||
				'Failed to create reminder',
		});
		dispatch({ type: SET_REMINDERS_LOADING, payload: false });
	}
};

/**
 * Saves changes to a reminder. The calendar shows the new time and assignees
 * straight away, puts them back if the save fails, and rethrows so the
 * caller can tell the user.
 */
export const updateReminder =
	(reminderId, _customer, updates) => async (dispatch, getState) => {
		const previous = getState().reminders.reminders.find(
			(reminder) => reminder._id === reminderId
		);
		if (previous) {
			const shown = ['dateTime', '_assignee', '_assignees', 'isCompleted'];
			dispatch({
				type: UPDATE_REMINDER,
				payload: {
					...previous,
					...Object.fromEntries(
						shown
							.filter((key) => Object.hasOwn(updates, key))
							.map((key) => [key, updates[key]])
					),
				},
			});
		}

		try {
			const response = await axios.post(
				`/api/reminders/update/${reminderId}`,
				{ _customer, updates }
			);
			if (!response.data?.reminder?._id) {
				throw new Error(
					response.data?.message || 'The reminder could not be saved.'
				);
			}
			dispatch({ type: UPDATE_REMINDER, payload: response.data.reminder });
			if (response.data.nextReminder) {
				dispatch({
					type: CREATE_REMINDER,
					payload: response.data.nextReminder,
				});
			}
			refreshRemindersPage(dispatch);
		} catch (error) {
			if (previous) dispatch({ type: UPDATE_REMINDER, payload: previous });
			dispatch({
				type: SET_REMINDERS_ERROR,
				payload: error.response?.data?.message || error.message,
			});
			throw error;
		}
	};

export const deleteReminder = (_reminder, _customer) => async (dispatch) => {
	try {
		dispatch({ type: SET_REMINDERS_LOADING, payload: true });
		if (!_customer) {
			await axios.post(`/api/reminders/delete/${_reminder}`);
		} else {
			await axios.post(`/api/reminders/delete/${_reminder}`, {
				_customer,
			});
		}
		dispatch({ type: DELETE_REMINDER, payload: _reminder });
		dispatch({ type: SET_REMINDERS_LOADING, payload: false });
		refreshRemindersPage(dispatch);
	} catch (error) {
		dispatch({ type: SET_REMINDERS_ERROR, payload: error.message });
		dispatch({ type: SET_REMINDERS_LOADING, payload: false });
		throw error;
	}
};

export const toggleReminderComplete = (reminder) => async (dispatch) => {
	try {
		// Optimistic update
		dispatch({
			type: UPDATE_REMINDER,
			payload: {
				...reminder,
				isCompleted: !reminder.isCompleted,
				completedAt: reminder.isCompleted
					? null
					: new Date().toISOString(),
			},
		});

		await axios.post(`/api/reminders/update/${reminder._id}`, {
			_customer: reminder.customer?._id || null,
			updates: {
				isCompleted: !reminder.isCompleted,
				completedAt: reminder.isCompleted
					? null
					: new Date().toISOString(),
			},
		});
		refreshRemindersPage(dispatch);
	} catch (error) {
		dispatch({ type: SET_REMINDERS_ERROR, payload: error.message });
		dispatch({
			type: UPDATE_REMINDER,
			payload: reminder,
		});
	}
};
